/**
 * CasaOmni navigation analytics.
 *
 * Events (sent to window.dataLayer, Shopify.analytics.publish and as DOM CustomEvents "casa:<name>"):
 *   nav_open          { menu, device, trigger }
 *   nav_click         { menu, group, label, position, device, target }
 *   nav_feature_click { menu, feature_title, device }
 *   nav_search_open   { source }
 *
 * Nothing here changes how the menu behaves; Horizon's own header-menu.js and header-drawer.js
 * keep handling hover, focus and drawer state.
 */

const isDrawer = (el) => Boolean(el.closest('.menu-drawer'));
const deviceOf = (el) => (isDrawer(el) ? 'mobile' : 'desktop');
const text = (el) => (el?.textContent || '').replace(/\s+/g, ' ').trim();

function publish(name, detail) {
  const payload = { ...detail, theme: 'casaomni-venezia' };
  try {
    window.dataLayer?.push({ event: name, ...payload });
  } catch (_) {
    /* analytics must never break navigation */
  }
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (_) {
    /* ignore */
  }
  document.dispatchEvent(new CustomEvent(`casa:${name}`, { detail: payload }));
}

function menuNameFor(link) {
  const casaMenu = link.closest('[data-casa-menu]')?.dataset.casaMenu;
  if (casaMenu) return casaMenu;
  const desktopItem = link.closest('.menu-list__list-item');
  if (desktopItem) return text(desktopItem.querySelector('.menu-list__link-title'));
  const drawerPanel = link.closest('.menu-drawer__menu-container');
  if (drawerPanel) return text(drawerPanel.querySelector('summary .menu-drawer__menu-item-text'));
  return '';
}

function groupNameFor(link) {
  const column = link.closest('.mega-menu__column');
  if (column) return text(column.querySelector('.casa-group-title'));
  const drawerGroup = link.closest('.menu-drawer__list-item');
  return text(drawerGroup?.querySelector('.menu-drawer__group-title'));
}

function positionFor(link) {
  const list = link.closest('ul');
  if (!list) return 0;
  return [...list.querySelectorAll(':scope > li')].findIndex((li) => li.contains(link)) + 1;
}

/* nav_click + nav_feature_click */
document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    const inNav = link.closest('.header-menu, .menu-drawer, .casa-mega__head');
    if (!inNav) return;

    const device = deviceOf(link);
    const target = link.dataset.casaNav || 'link';
    const menu = menuNameFor(link);

    if (target === 'feature') {
      publish('nav_feature_click', {
        menu,
        feature_title: text(link.querySelector('.casa-feature__title')),
        device,
      });
      return;
    }

    publish('nav_click', {
      menu,
      group: groupNameFor(link),
      label: text(link),
      position: positionFor(link),
      device,
      target,
    });
  },
  { capture: true, passive: true }
);

/* nav_open (desktop): the first pointer entry on a top-level item that has a panel, once per visit to the header */
let lastOpened = '';
document.addEventListener(
  'pointerenter',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('.menu-list__list-item');
    if (!item || !item.querySelector('.menu-list__submenu')) return;
    const menu = text(item.querySelector('.menu-list__link-title'));
    if (menu && menu !== lastOpened) {
      lastOpened = menu;
      publish('nav_open', { menu, device: 'desktop', trigger: 'hover' });
    }
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'pointerleave',
  (event) => {
    if (event.target instanceof Element && event.target.matches('header-menu')) lastOpened = '';
  },
  { capture: true, passive: true }
);

/* nav_open (keyboard / touch / drawer) */
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.menu-list__disclosure');
    if (disclosure) {
      publish('nav_open', {
        menu: text(disclosure.closest('.menu-list__list-item')?.querySelector('.menu-list__link-title')),
        device: 'desktop',
        trigger: 'click',
      });
      return;
    }
    const summary = event.target.closest('.menu-drawer__menu-item--mainlist');
    if (summary && summary.tagName === 'SUMMARY') {
      publish('nav_open', { menu: text(summary), device: 'mobile', trigger: 'tap' });
    }
  },
  { capture: true, passive: true }
);

/* nav_search_open (drawer search field) */
document.addEventListener(
  'focusin',
  (event) => {
    if (event.target instanceof Element && event.target.matches('.casa-drawer-search input')) {
      publish('nav_search_open', { source: 'drawer' });
    }
  },
  { passive: true }
);
